/* ==========================================================================
   Trade-In drawer
   4-step flow: Device selection > IMEI > Survey > Estimated price / Checkout.

   API layer talks to the store's Shopify App Proxy (signed automatically):
     GET {proxyPath}?action=trade-in-device-list&category=...
     GET {proxyPath}?action=trade-in-validate-imei&model_id=...&imei=...&category=...
     GET {proxyPath}?action=trade-in-get-survey-question&model_id=...&category=...
     GET {proxyPath}?action=trade-in-submit-survey&model_id=...&imei=...&storage=...&answers=[json]&category=...

   Every response uses the envelope { api: true, data } | { api: false, msg }.
   Contract quirks (per BE integration guide, live-confirmed 2026-07-31):
     - device-list returns model_id as a STRING; validate-imei returns it as a
       NUMBER — always compare as strings.
     - IMEI must pass a Luhn check (server rejects with "Luhn check error").
     - submit-survey has no "qualified" flag: price > 0 = qualified,
       price 0/absent = not qualified (rejection, no order exists yet).
     - get-survey-question can return api:true with empty data — means "no
       questions available", not an error.

   Checkout = ADD TO CART (client-confirmed), with two line item properties:
     _trade_in_amount             -> read by a Shopify Function to auto-apply
                                     the trade-in discount at cart/checkout
     _trade_in_survey_unique_code -> read by BE (orders/paid webhook) to
                                     trigger fulfillment with Laku6
   Both are required together. Max ONE trade-in item per order: the trigger
   button hides when the cart already holds one, and checkout re-checks the
   cart before adding.

   Mock mode (Theme settings > Trade-In) returns dummy payloads in the same
   envelope for previewing without the backend.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.getElementById('tradein-root');
  var configEl = document.getElementById('tradein-config');
  if (!root || !configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    console.error('[trade-in] invalid config JSON', e);
    return;
  }

  /* ------------------------------------------------------------------------
     Constants
     ------------------------------------------------------------------------ */

  // Device categories shown in the first dropdown. `value` maps to the
  // proxy's `category` param (smartphone | non-smartphone).
  var CATEGORIES = [
    { value: 'smartphone', label: 'Phone' }
    //{ value: 'non-smartphone', label: 'Tablet & Others' }
  ];

  // The API returns prices in major currency units (live-confirmed: price 150
  // produced a S$150.00 cart discount). Shopify money formatting works in
  // cents, so multiply for display only — the raw API price is what goes into
  // the _trade_in_amount cart property.
  var PRICE_MULTIPLIER = 100;

  // Cart line item properties required together on the trade-in product.
  var PROP_AMOUNT = '_trade_in_amount';
  var PROP_SURVEY_CODE = '_trade_in_survey_unique_code';
  // Display-only — read by trade-in-button.liquid for the "Trade-in applied"
  // summary row. Not part of the BE/Function contract above.
  var PROP_DEVICE_NAME = '_trade_in_device_name';

  // Short labels for the "Your Old Device Detail" rows, keyed by question_key.
  // Unknown keys fall back to a humanised version of the key.
  var QUESTION_SHORT_LABELS = {
    body_condition: 'Physical',
    screen_condition: 'Screen',
    screen_display_condition: 'Display',
    display_condition: 'Display',
    camera_condition: 'Camera',
    keyboard_condition: 'Keyboard',
    box_condition: 'Box',
    additional_damage_fingerprint: 'Fingerprint Sensor',
    additional_damage_button: 'Buttons',
    additional_damage_touchscreen: 'Touchscreen',
    additional_damage_camera: 'Camera',
    additional_damage_signal: 'Signal',
    additional_damage_charge: 'Charging',
    additional_damage_stylus: 'Stylus'
  };

  var STEP_TITLES = {
    1: 'Select the Device Model You’re Selling',
    2: 'Enter your Device IMEI',
    3: 'Tell Us About Your Phone',
    4: 'Estimated Total Payment'
  };

  var STEP_CTA_LABELS = { 1: 'Continue', 2: 'Continue', 3: 'Check Price', 4: 'Checkout' };

  var GENERIC_ERROR = 'Something went wrong. Please try again.';

  /* ------------------------------------------------------------------------
     Mock API — response shapes mirror the app proxy contract exactly
     ------------------------------------------------------------------------ */

  var MOCK_SURVEY_IMG = 'https://cdn.maujual.com/public/images/survey/';

  // model_id is a STRING here, matching the live device-list response.
  var MOCK_DEVICE_LIST = {
    smartphone: [
      {
        name: 'Apple iPhone 15',
        product_type: 'smartphone',
        brand: 'Apple',
        image_url: 'https://cdn.maujual.com/public/images/device-category/smartphone.webp',
        variant_combinations: [
          { model_name: 'Apple iPhone 15 128gb', min_price: 550, max_price: 700, currency: { symbol: 'S$' }, model_id: '4101', components: [{ code: 'brand', value: 'Apple' }, { code: 'model_name', value: 'iPhone 15' }, { code: 'storage', value: '128gb' }] },
          { model_name: 'Apple iPhone 15 256gb', min_price: 620, max_price: 780, currency: { symbol: 'S$' }, model_id: '4102', components: [{ code: 'brand', value: 'Apple' }, { code: 'model_name', value: 'iPhone 15' }, { code: 'storage', value: '256gb' }] },
          { model_name: 'Apple iPhone 15 512gb', min_price: 700, max_price: 880, currency: { symbol: 'S$' }, model_id: '4103', components: [{ code: 'brand', value: 'Apple' }, { code: 'model_name', value: 'iPhone 15' }, { code: 'storage', value: '512gb' }] }
        ]
      },
      {
        name: 'Realme Realme 8 Pro',
        product_type: 'smartphone',
        brand: 'Realme',
        image_url: 'https://cdn.maujual.com/public/images/device-category/smartphone.webp',
        variant_combinations: [
          { model_name: 'Realme Realme 8 Pro 128gb', min_price: 90, max_price: 95, currency: { symbol: 'S$' }, model_id: '2830', components: [{ code: 'brand', value: 'Realme' }, { code: 'model_name', value: 'Realme 8 Pro' }, { code: 'storage', value: '128gb' }] }
        ]
      },
      {
        name: 'Samsung Galaxy S24',
        product_type: 'smartphone',
        brand: 'Samsung',
        image_url: 'https://cdn.maujual.com/public/images/device-category/smartphone.webp',
        variant_combinations: [
          { model_name: 'Samsung Galaxy S24 256gb', min_price: 420, max_price: 560, currency: { symbol: 'S$' }, model_id: '4201', components: [{ code: 'brand', value: 'Samsung' }, { code: 'model_name', value: 'Galaxy S24' }, { code: 'storage', value: '256gb' }] },
          { model_name: 'Samsung Galaxy S24 512gb', min_price: 480, max_price: 630, currency: { symbol: 'S$' }, model_id: '4202', components: [{ code: 'brand', value: 'Samsung' }, { code: 'model_name', value: 'Galaxy S24' }, { code: 'storage', value: '512gb' }] }
        ]
      }
    ],
    'non-smartphone': [
      {
        name: 'Apple iPad Air 11 M2',
        product_type: 'tablet_and_ipad',
        brand: 'Apple',
        image_url: 'https://cdn.maujual.com/public/images/device-category/tablet.webp',
        variant_combinations: [
          { model_name: 'Apple iPad Air 11 M2 128gb', min_price: 380, max_price: 520, currency: { symbol: 'S$' }, model_id: '5301', components: [{ code: 'brand', value: 'Apple' }, { code: 'model_name', value: 'iPad Air 11 M2' }, { code: 'storage', value: '128gb' }] },
          { model_name: 'Apple iPad Air 11 M2 256gb', min_price: 430, max_price: 580, currency: { symbol: 'S$' }, model_id: '5302', components: [{ code: 'brand', value: 'Apple' }, { code: 'model_name', value: 'iPad Air 11 M2' }, { code: 'storage', value: '256gb' }] }
        ]
      },
      {
        name: 'Samsung Galaxy Tab S9',
        product_type: 'tablet_and_ipad',
        brand: 'Samsung',
        image_url: 'https://cdn.maujual.com/public/images/device-category/tablet.webp',
        variant_combinations: [
          { model_name: 'Samsung Galaxy Tab S9 128gb', min_price: 320, max_price: 450, currency: { symbol: 'S$' }, model_id: '5401', components: [{ code: 'brand', value: 'Samsung' }, { code: 'model_name', value: 'Galaxy Tab S9' }, { code: 'storage', value: '128gb' }] }
        ]
      }
    ]
  };

  // Question keys mirror the live catalog (per the E2E test script) so the
  // mock exercises the same rendering paths — answer_image URLs point at the
  // API's own CDN, exactly as the live responses do.
  var MOCK_SURVEY_QUESTIONS = [
    {
      question_key: 'body_condition',
      question_bodytext: 'Describe the Physical Condition of the Device',
      position: 1,
      answers: [
        { answer_key: 'perfect', answer_bodytext: 'No defect', answer_image: MOCK_SURVEY_IMG + 'body-no-defect.png', answer_position: 1 },
        { answer_key: 'lightly_used', answer_bodytext: '1-3 scratches', answer_image: MOCK_SURVEY_IMG + 'body-1-3-scratch.png', answer_position: 2 },
        { answer_key: 'well_used', answer_bodytext: '5 scratches', answer_image: MOCK_SURVEY_IMG + 'body-5-scratch.png', answer_position: 3 },
        { answer_key: 'heavily_used', answer_bodytext: 'Has cracks/dents', answer_image: MOCK_SURVEY_IMG + 'body-cracks.png', answer_position: 4 }
      ]
    },
    {
      question_key: 'screen_condition',
      question_bodytext: 'Describe the Condition of the Physical Screen',
      position: 2,
      answers: [
        { answer_key: 'perfect', answer_bodytext: 'Good as new', answer_image: '', answer_position: 1 },
        { answer_key: 'minor_scratches', answer_bodytext: 'Minor scratches', answer_image: '', answer_position: 2 },
        { answer_key: 'obvious_scratches', answer_bodytext: 'Obvious scratches', answer_image: '', answer_position: 3 }
      ]
    },
    {
      question_key: 'display_condition',
      question_bodytext: 'Describe the Condition of the Display',
      position: 3,
      answers: [
        { answer_key: 'perfect', answer_bodytext: 'Good as new', answer_image: '', answer_position: 1 },
        { answer_key: 'light_discoloration', answer_bodytext: 'Light discoloration', answer_image: '', answer_position: 2 },
        { answer_key: 'major_discoloration', answer_bodytext: 'Major discoloration', answer_image: '', answer_position: 3 }
      ]
    },
    {
      question_key: 'additional_damage_camera',
      question_bodytext: 'Is the Camera Working Normally?',
      position: 4,
      answers: [
        { answer_key: 'pass', answer_bodytext: 'No issues', answer_image: '', answer_position: 1 },
        { answer_key: 'failed', answer_bodytext: 'Has issues', answer_image: '', answer_position: 2 }
      ]
    },
    {
      question_key: 'additional_damage_button',
      question_bodytext: 'Are the Buttons Working Normally?',
      position: 5,
      answers: [
        { answer_key: 'pass', answer_bodytext: 'No issues', answer_image: '', answer_position: 1 },
        { answer_key: 'failed', answer_bodytext: 'Has issues', answer_image: '', answer_position: 2 }
      ]
    },
    {
      question_key: 'box_condition',
      question_bodytext: 'Do You Still Have the Original Box?',
      position: 6,
      answers: [
        { answer_key: 'pass', answer_bodytext: 'Yes', answer_image: '', answer_position: 1 },
        { answer_key: 'failed', answer_bodytext: 'No', answer_image: '', answer_position: 2 }
      ]
    }
  ];

  function mockResponse(action, params) {
    switch (action) {
      case 'trade-in-device-list':
        return { api: true, data: MOCK_DEVICE_LIST[params.category] || [] };

      case 'trade-in-validate-imei': {
        if (!luhnValid(params.imei || '')) {
          return { api: false, msg: 'Luhn check error ' };
        }
        return {
          api: true,
          data: { model_group: 'Mock Model Group', model_name: 'Mock Model', model: 'Mock Model', brand: 'Mock', model_id: Number(params.model_id) }
        };
      }

      case 'trade-in-get-survey-question':
        return { api: true, data: MOCK_SURVEY_QUESTIONS };

      case 'trade-in-submit-survey':
        // Mock a rejection with model_id '2830' (Realme 8 Pro) for testing
        // the not-qualified path; anything else qualifies at S$150.
        if (String(params.model_id) === '2830') {
          return { api: true, data: { survey_unique_code: 'mockrej1', price: 0 } };
        }
        return { api: true, data: { survey_unique_code: 'a1b2c3d4', price: 150 } };

      default:
        return { api: false, msg: 'Unknown action' };
    }
  }

  // Single request gateway — mock and live paths resolve to the same
  // envelope: { api: true, data } | { api: false, msg }.
  function apiCall(action, params) {
    if (config.mockMode) {
      return new Promise(function (resolve) {
        // Small delay to exercise the loading states realistically.
        setTimeout(function () {
          resolve(mockResponse(action, params));
        }, 350);
      });
    }
    var search = new URLSearchParams(params || {});
    search.set('action', action);
    var base = (config.proxyPath || '/apps/integration').replace(/\/$/, '');
    return fetch(base + '?' + search.toString(), {
      method: 'GET',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      return res.json();
    });
  }

  // IMEIs are Luhn-checksummed; pre-checking locally gives instant feedback
  // and saves a proxy round-trip (the server still enforces it).
  function luhnValid(digits) {
    if (!/^\d{15}$/.test(digits)) return false;
    var sum = 0;
    for (var i = 0; i < digits.length; i++) {
      var d = Number(digits.charAt(digits.length - 1 - i));
      if (i % 2 === 1) {
        d *= 2;
        if (d > 9) d -= 9;
      }
      sum += d;
    }
    return sum % 10 === 0;
  }

  // The cart line's variant is fixed for the whole drawer session (set on
  // open from the clicked trigger's data attributes) — no picker to sync with.
  function currentVariant() {
    return { id: config.product.variantId, price: config.product.price };
  }

  // Only one trade-in item is allowed per order.
  function cartHasTradeIn() {
    return fetch('/cart.js')
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        return (cart.items || []).some(function (item) {
          return item.properties && (item.properties[PROP_SURVEY_CODE] || item.properties[PROP_AMOUNT]);
        });
      })
      .catch(function () {
        return false;
      });
  }

  /* ------------------------------------------------------------------------
     Money formatting
     ------------------------------------------------------------------------ */

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, config.moneyFormat);
    }
    // Minimal fallback covering the common Shopify money format placeholders.
    var amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var noDecimals = Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (config.moneyFormat || '${{amount}}')
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount);
  }

  /* ------------------------------------------------------------------------
     State
     ------------------------------------------------------------------------ */

  // Fresh per drawer-open — see resetState(), since the drawer is a shared
  // shell reused across every eligible cart line, not bound to one product.
  function freshState() {
    return {
      step: 1,
      devices: [], // device-list response for the chosen category
      category: '',
      brand: '',
      deviceName: '',
      variant: null, // chosen variant_combination (has model_id + storage)
      imei: '',
      questions: [],
      answers: {}, // question_key -> answer_key
      result: null, // { survey_unique_code, price } from submit-survey
      rejected: false, // device declared ineligible — step 4 shows the rejection state
      lineKey: null // cart line being replaced on checkout (cart context only)
    };
  }

  var state = freshState();

  /* ------------------------------------------------------------------------
     Element lookups
     ------------------------------------------------------------------------ */

  var $ = function (sel) {
    return root.querySelector(sel);
  };
  var $$ = function (sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  };

  var els = {
    title: $('[data-tradein-title]'),
    cta: $('[data-tradein-next]'),
    selects: {
      category: $('[data-tradein-select="category"]'),
      brand: $('[data-tradein-select="brand"]'),
      model: $('[data-tradein-select="model"]'),
      storage: $('[data-tradein-select="storage"]')
    },
    imeiInput: $('[data-tradein-imei]'),
    imeiError: $('[data-tradein-imei-error]'),
    survey: $('[data-tradein-survey]'),
    surveyError: $('[data-tradein-survey-error]'),
    newPrice: $('[data-tradein-new-price]'),
    oldPrice: $('[data-tradein-old-price]'),
    deviceDetail: $('[data-tradein-device-detail]'),
    summaryProductPrice: $('[data-tradein-summary-product-price]'),
    oldName: $('[data-tradein-old-name]'),
    deduction: $('[data-tradein-deduction]'),
    total: $('[data-tradein-total]'),
    agree: $('[data-tradein-agree]'),
    checkoutError: $('[data-tradein-checkout-error]'),
    result: $('[data-tradein-result]'),
    rejection: $('[data-tradein-rejection]'),
    rejectionMsg: $('[data-tradein-rejection-msg]'),
    rejectionActions: $('[data-tradein-rejection-actions]'),
    productImage: $('[data-tradein-product-image]'),
    productTitle: $('[data-tradein-product-title]')
  };

  function showError(el, message) {
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  function hideError(el) {
    if (el) el.hidden = true;
  }

  // Rejection = a step-4 state (per design): product card + ineligible notice,
  // footer swaps CHECKOUT for Continue with Purchase / Cancel Trade-In.
  var REJECTION_DEFAULT_MSG = 'Sorry, the condition(s) you have declared make your device ineligible for trade-in';

  function showRejection(message) {
    els.rejectionMsg.textContent = message || REJECTION_DEFAULT_MSG;
    state.rejected = true;
    showStep(4);
  }

  // Toggles step 4 between its two modes (eligible result vs rejection).
  // No-op visual state on other steps — the elements live inside step 4.
  function applyStep4Mode() {
    var rejected = state.step === 4 && state.rejected;
    els.rejection.hidden = !rejected;
    els.result.hidden = rejected;
    els.rejectionActions.hidden = !rejected;
    els.cta.hidden = rejected;
  }

  /* ------------------------------------------------------------------------
     Rendering helpers
     ------------------------------------------------------------------------ */

  function humanise(key) {
    return key.replace(/_/g, ' ').replace(/\b\w/g, function (c) {
      return c.toUpperCase();
    });
  }

  function prettyStorage(value) {
    return String(value).toUpperCase().replace(/(\d+)\s*GB/, '$1 GB').replace(/(\d+)\s*TB/, '$1 TB');
  }

  function fillSelect(select, options, placeholder) {
    select.innerHTML = '';
    var ph = document.createElement('option');
    ph.value = '';
    ph.disabled = true;
    ph.selected = true;
    ph.textContent = placeholder;
    select.appendChild(ph);
    options.forEach(function (opt) {
      var o = document.createElement('option');
      o.value = opt.value;
      o.textContent = opt.label;
      select.appendChild(o);
    });
    select.classList.add('is-placeholder');
  }

  function showField(name, show) {
    var field = $('[data-tradein-field="' + name + '"]');
    if (field) field.hidden = !show;
  }

  function setCtaLoading(loading) {
    els.cta.classList.toggle('is-loading', loading);
  }

  function showStep(step) {
    state.step = step;
    $$('[data-tradein-step]').forEach(function (section) {
      section.hidden = section.getAttribute('data-tradein-step') !== String(step);
    });
    $$('[data-tradein-pill]').forEach(function (pill) {
      pill.classList.toggle('is-active', Number(pill.getAttribute('data-tradein-pill')) <= step);
    });
    els.title.textContent = STEP_TITLES[step];
    els.cta.textContent = STEP_CTA_LABELS[step];
    els.cta.classList.toggle('tradein-cta-checkout', step === 4);
    applyStep4Mode();
    $('.tradein-body').scrollTop = 0;
    updateCta();
  }

  function updateCta() {
    var enabled = false;
    switch (state.step) {
      case 1:
        enabled = !!(state.category && state.brand && state.deviceName && state.variant);
        break;
      case 2:
        enabled = /^\d{15}$/.test(els.imeiInput.value.trim());
        break;
      case 3:
        enabled =
          state.questions.length > 0 &&
          state.questions.every(function (q) {
            return !!state.answers[q.question_key];
          });
        break;
      case 4:
        enabled = els.agree.checked;
        break;
    }
    els.cta.disabled = !enabled;
  }

  /* ------------------------------------------------------------------------
     Step 1 — device selection
     ------------------------------------------------------------------------ */

  function initCategorySelect() {
    fillSelect(
      els.selects.category,
      CATEGORIES.map(function (c) {
        return { value: c.value, label: c.label };
      }),
      'Select Device Category'
    );
  }

  // freshState() wipes the JS state on reopen, but the step-1 DOM (cascading
  // selects populated by fillSelect(), field visibility toggled by
  // showField()) survives a close() untouched — leaving stale options/values
  // bound to an empty state. Re-run on every open() so a half-filled session
  // never leaks into the next one.
  function resetStep1Fields() {
    initCategorySelect();
    ['brand', 'model', 'storage'].forEach(function (name) {
      showField(name, false);
      els.selects[name].innerHTML = '';
    });
    els.imeiInput.value = '';
    els.agree.checked = false;
    hideError(els.imeiError);
  }

  function onCategoryChange(category) {
    state.category = category;
    state.brand = '';
    state.deviceName = '';
    state.variant = null;
    showField('brand', false);
    showField('model', false);
    showField('storage', false);
    updateCta();

    setCtaLoading(true);
    apiCall('trade-in-device-list', { category: category })
      .then(function (res) {
        setCtaLoading(false);
        if (!res.api) {
          console.error('[trade-in] device-list failed', res);
          alert(res.msg || GENERIC_ERROR);
          return;
        }
        state.devices = res.data || [];
        var brands = [];
        state.devices.forEach(function (d) {
          if (brands.indexOf(d.brand) === -1) brands.push(d.brand);
        });
        fillSelect(
          els.selects.brand,
          brands.map(function (b) {
            return { value: b, label: b };
          }),
          'Select Brand'
        );
        showField('brand', true);
      })
      .catch(function (err) {
        setCtaLoading(false);
        console.error('[trade-in] device-list error', err);
        alert(GENERIC_ERROR);
      });
  }

  function onBrandChange(brand) {
    state.brand = brand;
    state.deviceName = '';
    state.variant = null;
    showField('storage', false);
    updateCta();

    var models = state.devices.filter(function (d) {
      return d.brand === brand;
    });
    fillSelect(
      els.selects.model,
      models.map(function (d) {
        return { value: d.name, label: d.name };
      }),
      state.category === 'smartphone' ? 'Select Phone Model' : 'Select Model'
    );
    showField('model', true);
  }

  function onModelChange(deviceName) {
    state.deviceName = deviceName;
    state.variant = null;
    updateCta();

    var device = state.devices.filter(function (d) {
      return d.name === deviceName;
    })[0];
    var variants = (device && device.variant_combinations) || [];
    fillSelect(
      els.selects.storage,
      variants.map(function (v) {
        var storage = (v.components || []).filter(function (c) {
          return c.code === 'storage';
        })[0];
        var value = storage ? storage.value : v.model_name;
        // model_id is a string in device-list — keep it verbatim as the value.
        return { value: String(v.model_id), label: prettyStorage(value) };
      }),
      'Select Storage'
    );
    showField('storage', true);
  }

  function onStorageChange(modelId) {
    var device = state.devices.filter(function (d) {
      return d.name === state.deviceName;
    })[0];
    state.variant =
      ((device && device.variant_combinations) || []).filter(function (v) {
        return String(v.model_id) === modelId;
      })[0] || null;
    updateCta();
  }

  /* ------------------------------------------------------------------------
     Step 3 — survey
     ------------------------------------------------------------------------ */

  function renderSurvey() {
    els.survey.innerHTML = '';
    hideError(els.surveyError);
    state.questions
      .slice()
      .sort(function (a, b) {
        return (a.position || 0) - (b.position || 0);
      })
      .forEach(function (q, index) {
        var card = document.createElement('div');
        card.className = 'tradein-card';

        var title = document.createElement('p');
        title.className = 'tradein-question-title';
        title.textContent = index + 1 + '. ' + q.question_bodytext;
        card.appendChild(title);

        var grid = document.createElement('div');
        grid.className = 'tradein-answers';
        (q.answers || [])
          .slice()
          .sort(function (a, b) {
            return (a.answer_position || 0) - (b.answer_position || 0);
          })
          .forEach(function (a) {
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'tradein-answer';
            btn.setAttribute('data-question', q.question_key);
            btn.setAttribute('data-answer', a.answer_key);

            var label = document.createElement('div');
            label.textContent = a.answer_bodytext;
            btn.appendChild(label);

            // Images come exclusively from the API's answer_image — the theme
            // hosts no survey illustrations of its own.
            if (a.answer_image) {
              var img = document.createElement('img');
              img.src = a.answer_image;
              img.alt = a.answer_bodytext;
              img.loading = 'lazy';
              img.onerror = function () {
                img.style.display = 'none';
              };
              btn.appendChild(img);
            }
            grid.appendChild(btn);
          });
        card.appendChild(grid);
        els.survey.appendChild(card);
      });
  }

  function onAnswerClick(btn) {
    var questionKey = btn.getAttribute('data-question');
    state.answers[questionKey] = btn.getAttribute('data-answer');
    hideError(els.surveyError);
    // Changing an answer lets the shopper retry after a rejection.
    state.rejected = false;
    $$('.tradein-answer[data-question="' + questionKey + '"]').forEach(function (el) {
      el.classList.toggle('is-selected', el === btn);
    });
    updateCta();
  }

  /* ------------------------------------------------------------------------
     Step 4 — estimated total payment
     ------------------------------------------------------------------------ */

  function answerLabel(question, answerKey) {
    var match = (question.answers || []).filter(function (a) {
      return a.answer_key === answerKey;
    })[0];
    return match ? match.answer_bodytext : answerKey;
  }

  function renderResult() {
    var productPriceCents = currentVariant().price;
    var tradeInCents = Math.round(state.result.price * PRICE_MULTIPLIER);
    var totalCents = Math.max(0, productPriceCents - tradeInCents);

    els.newPrice.textContent = formatMoney(totalCents);
    els.oldPrice.textContent = formatMoney(productPriceCents);
    els.summaryProductPrice.textContent = formatMoney(productPriceCents);
    els.oldName.textContent = state.deviceName;
    els.deduction.textContent = '-' + formatMoney(tradeInCents);
    els.total.textContent = formatMoney(totalCents);

    // Old device detail rows: model + one row per answered survey question.
    els.deviceDetail.innerHTML = '';
    var rows = [{ label: 'Device Model', value: state.variant.model_name }];
    state.questions.forEach(function (q) {
      rows.push({
        label: QUESTION_SHORT_LABELS[q.question_key] || humanise(q.question_key),
        value: answerLabel(q, state.answers[q.question_key])
      });
    });
    rows.forEach(function (row) {
      var div = document.createElement('div');
      div.className = 'tradein-row';
      var label = document.createElement('span');
      label.className = 'tradein-row-label';
      label.textContent = row.label;
      var value = document.createElement('span');
      value.className = 'tradein-row-value';
      value.textContent = row.value;
      div.appendChild(label);
      div.appendChild(value);
      els.deviceDetail.appendChild(div);
    });
  }

  /* ------------------------------------------------------------------------
     Step transitions
     ------------------------------------------------------------------------ */

  function goToImei() {
    hideError(els.imeiError);
    showStep(2);
  }

  function validateImeiAndContinue() {
    var imei = els.imeiInput.value.trim();
    hideError(els.imeiError);

    // Local Luhn pre-check — the server rejects these anyway ("Luhn check
    // error"), this just gives instant feedback without a round-trip.
    if (!luhnValid(imei)) {
      showError(els.imeiError, 'This IMEI is not valid. Please double-check the 15 digits and try again.');
      return;
    }

    setCtaLoading(true);
    apiCall('trade-in-validate-imei', {
      model_id: state.variant.model_id,
      imei: imei,
      category: state.category
    })
      .then(function (res) {
        if (!res.api) {
          setCtaLoading(false);
          showError(els.imeiError, res.msg || 'IMEI could not be validated. Please check and try again.');
          return;
        }
        state.imei = imei;
        return loadSurvey();
      })
      .catch(function (err) {
        setCtaLoading(false);
        console.error('[trade-in] validate-imei error', err);
        showError(els.imeiError, GENERIC_ERROR);
      });
  }

  function loadSurvey() {
    return apiCall('trade-in-get-survey-question', {
      model_id: state.variant.model_id,
      category: state.category
    }).then(function (res) {
      // Debug: raw response, to verify whether answers include answer_image.
      console.log('[trade-in] get-survey-question raw response:', JSON.stringify(res, null, 2));
      setCtaLoading(false);
      if (!res.api) {
        showError(els.imeiError, res.msg || 'Could not load the survey. Please try again.');
        return;
      }
      // api:true with empty data = no questions configured for this model —
      // the flow cannot continue without answers (submit requires them).
      if (!res.data || !res.data.length) {
        showError(els.imeiError, 'Trade-in is not available for this device at the moment. Please try another device or contact us for assistance.');
        return;
      }
      state.questions = res.data;
      state.answers = {};
      renderSurvey();
      showStep(3);
    });
  }

  function submitSurveyAndContinue() {
    var storage = ((state.variant.components || []).filter(function (c) {
      return c.code === 'storage';
    })[0] || {}).value;

    hideError(els.surveyError);
    setCtaLoading(true);
    apiCall('trade-in-submit-survey', {
      model_id: state.variant.model_id,
      imei: state.imei,
      storage: storage,
      category: state.category,
      answers: JSON.stringify(
        state.questions.map(function (q) {
          return { question_key: q.question_key, option_key: state.answers[q.question_key] };
        })
      )
    })
      .then(function (res) {
        setCtaLoading(false);
        if (!res.api) {
          console.error('[trade-in] submit-survey failed', res);
          // Known parameter/processing errors are retryable — plain error.
          // Anything else is Laku6's own rejection message passed through
          // (e.g. "…does not meet the required conditions due to problems
          // in: …") — show the rejection screen.
          var msg = res.msg || '';
          if (!msg || /not able to process|missing |invalid answers|failed to submit/i.test(msg)) {
            showError(els.surveyError, msg || 'Could not calculate the trade-in price. Please try again.');
            return;
          }
          showRejection(msg);
          return;
        }
        // No "qualified" flag in the contract — price > 0 means qualified.
        if (!res.data || !(Number(res.data.price) > 0)) {
          showRejection();
          return;
        }
        // survey_unique_code + price must be retained — they become the cart
        // properties BE reconciles against.
        state.result = res.data;
        state.rejected = false;
        els.agree.checked = false;
        hideError(els.checkoutError);
        renderResult();
        showStep(4);
      })
      .catch(function (err) {
        setCtaLoading(false);
        console.error('[trade-in] submit-survey error', err);
        showError(els.surveyError, GENERIC_ERROR);
      });
  }

  // Cart context: the shopper already added this product normally. Line
  // properties are immutable once set, so "updating" the line means dropping
  // one unit from the plain line, then adding a new line that carries the
  // trade-in properties. Resolves with the ORIGINAL line's own properties
  // (e.g. _group_id/_group_parent linking it to attached GWP/PWP/addon/
  // warranty children) so checkout() can carry them over — otherwise the new
  // line starts group-less and those children end up orphaned: their PWP/GWP
  // pricing depends on matching a parent's _group_id, and removeItemCart's
  // cascade-delete can no longer find them without it.
  function replaceCartLine() {
    if (!state.lineKey) return Promise.resolve(null);
    return fetch('/cart.js')
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var line = (cart.items || []).filter(function (item) {
          return item.key === state.lineKey;
        })[0];
        if (!line) return null;
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: state.lineKey, quantity: line.quantity - 1 })
        }).then(function () {
          return line.properties || {};
        });
      });
  }

  // Checkout = add to cart (client-confirmed) with the two required line item
  // properties. On the cart (state.lineKey set) the plain line is replaced
  // first; then the page reloads in place so the cart list reflects both
  // changes. On a standalone drawer (no lineKey) it continues to '/cart'
  // where the Shopify Function shows the trade-in discount. Only one
  // trade-in item per order.
  function checkout() {
    hideError(els.checkoutError);
    setCtaLoading(true);

    cartHasTradeIn()
      .then(function (alreadyInCart) {
        if (alreadyInCart) {
          setCtaLoading(false);
          document.body.classList.add('tradein-in-cart');
          showError(els.checkoutError, 'A trade-in item is already in your cart. Only one trade-in is allowed per order.');
          return null;
        }
        return replaceCartLine()
          .then(function (originalProperties) {
            // Carry over the original line's own properties (_group_id,
            // _group_parent, etc.) so its attached GWP/PWP/addon/warranty
            // children stay linked to the new line instead of being orphaned.
            var properties = {};
            for (var key in originalProperties) {
              if (Object.prototype.hasOwnProperty.call(originalProperties, key)) {
                properties[key] = originalProperties[key];
              }
            }
            properties[PROP_AMOUNT] = state.result.price;
            properties[PROP_SURVEY_CODE] = state.result.survey_unique_code;
            properties[PROP_DEVICE_NAME] = state.deviceName;
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                items: [{ id: currentVariant().id, quantity: 1, properties: properties }]
              })
            });
          })
          .then(function (res) {
            return res.json().then(function (json) {
              return { ok: res.ok, json: json };
            });
          });
      })
      .then(function (result) {
        if (!result) return; // blocked by the one-per-order guard
        setCtaLoading(false);
        if (!result.ok) {
          console.error('[trade-in] cart add failed', result.json);
          showError(els.checkoutError, (result.json && result.json.description) || GENERIC_ERROR);
          return;
        }
        document.dispatchEvent(
          new CustomEvent('tradein:checkout', {
            detail: {
              surveyUniqueCode: state.result.survey_unique_code,
              tradeInPrice: state.result.price,
              modelId: state.variant.model_id,
              modelName: state.variant.model_name,
              imei: state.imei,
              answers: state.answers,
              productId: config.product.id,
              variantId: currentVariant().id
            }
          })
        );
        if (state.lineKey) {
          window.location.reload();
        } else {
          window.location.href = '/cart';
        }
      })
      .catch(function (err) {
        setCtaLoading(false);
        console.error('[trade-in] checkout error', err);
        showError(els.checkoutError, GENERIC_ERROR);
      });
  }

  /* ------------------------------------------------------------------------
     Open / close / navigation
     ------------------------------------------------------------------------ */

  // `trigger` is the clicked [data-tradein-open] button — its data attributes
  // (rendered per cart line by trade-in-button.liquid) tell the shared drawer
  // which product/variant/cart-line it's now operating on.
  function open(trigger) {
    if (trigger) {
      state = freshState();
      resetStep1Fields();
      state.lineKey = trigger.getAttribute('data-tradein-line-key') || null;
      config.product = {
        id: trigger.getAttribute('data-tradein-product-id'),
        variantId: Number(trigger.getAttribute('data-tradein-variant-id')),
        price: Number(trigger.getAttribute('data-tradein-price')),
        title: trigger.getAttribute('data-tradein-title'),
        image: trigger.getAttribute('data-tradein-image')
      };
      els.productTitle.textContent = config.product.title || '';
      els.productImage.hidden = !config.product.image;
      if (config.product.image) {
        els.productImage.src = config.product.image;
        els.productImage.alt = config.product.title || '';
      }
      showStep(1);
    }
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('tradein-lock');
  }

  function close() {
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('tradein-lock');
  }

  function back() {
    if (state.step > 1) {
      showStep(state.step - 1);
    } else {
      close();
    }
  }

  function next() {
    switch (state.step) {
      case 1:
        goToImei();
        break;
      case 2:
        validateImeiAndContinue();
        break;
      case 3:
        submitSurveyAndContinue();
        break;
      case 4:
        checkout();
        break;
    }
  }

  /* ------------------------------------------------------------------------
     Events
     ------------------------------------------------------------------------ */

  // Open triggers use delegation: there's one button per eligible cart line,
  // re-rendered whenever the cart section updates, so direct listeners bound
  // at init time would be lost.
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-tradein-open]');
    if (trigger) open(trigger);
  });

  // "Remove" on an already-applied trade-in line: line properties are
  // immutable, so undoing means dropping this line and re-adding a plain one
  // for the same variant/quantity, then reloading so the ADD card comes back.
  // The rest of the line's own properties (_group_id/_group_parent linking
  // attached GWP/PWP/addon/warranty children) carry over minus the trade-in
  // ones — otherwise those children lose their parent, same issue as checkout().
  document.addEventListener('click', function (e) {
    var removeLink = e.target.closest && e.target.closest('[data-tradein-remove]');
    if (!removeLink) return;
    e.preventDefault();
    var lineKey = removeLink.getAttribute('data-tradein-remove-line-key');
    var variantId = removeLink.getAttribute('data-tradein-remove-variant-id');
    var qty = Number(removeLink.getAttribute('data-tradein-remove-qty')) || 1;
    fetch('/cart.js')
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var line = (cart.items || []).filter(function (item) {
          return item.key === lineKey;
        })[0];
        var properties = {};
        var originalProperties = (line && line.properties) || {};
        for (var key in originalProperties) {
          if (
            Object.prototype.hasOwnProperty.call(originalProperties, key) &&
            key !== PROP_AMOUNT &&
            key !== PROP_SURVEY_CODE &&
            key !== PROP_DEVICE_NAME
          ) {
            properties[key] = originalProperties[key];
          }
        }
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: 0 })
        }).then(function () {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity: qty, properties: properties }] })
          });
        });
      })
      .then(function () {
        window.location.reload();
      })
      .catch(function (err) {
        console.error('[trade-in] undo error', err);
      });
  });

  root.addEventListener('click', function (e) {
    var t = e.target;
    if (t.closest('[data-tradein-close]')) return close();
    if (t.closest('[data-tradein-back]')) return back();
    var answer = t.closest('.tradein-answer');
    if (answer) return onAnswerClick(answer);
    var accordion = t.closest('[data-tradein-accordion]');
    if (accordion) {
      accordion.setAttribute('aria-expanded', accordion.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
    }
  });

  els.cta.addEventListener('click', function () {
    if (!els.cta.disabled) next();
  });

  Object.keys(els.selects).forEach(function (name) {
    els.selects[name].addEventListener('change', function () {
      this.classList.remove('is-placeholder');
      var value = this.value;
      if (name === 'category') onCategoryChange(value);
      if (name === 'brand') onBrandChange(value);
      if (name === 'model') onModelChange(value);
      if (name === 'storage') onStorageChange(value);
    });
  });

  els.imeiInput.addEventListener('input', function () {
    this.value = this.value.replace(/\D/g, '');
    hideError(els.imeiError);
    updateCta();
  });

  els.agree.addEventListener('change', updateCta);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && root.classList.contains('is-open')) close();
  });

  /* ------------------------------------------------------------------------
     Init
     ------------------------------------------------------------------------ */

  initCategorySelect();
  showStep(1);

  // Hide every cart-line trigger when the cart already holds a trade-in item
  // (max one per order). The class lives on <body> so it covers every line's
  // button regardless of how the cart section gets re-rendered.
  function refreshTradeInCartState() {
    cartHasTradeIn().then(function (has) {
      document.body.classList.toggle('tradein-in-cart', has);
    });
  }

  refreshTradeInCartState();

  // The cart page swaps #cart-content's innerHTML via AJAX on every add/remove/
  // qty change (see removeItemCart/refreshCartPage in cart-content.liquid) —
  // this script only runs once, so without this the class above would go
  // stale the moment the shopper adds or removes the trade-in line without a
  // full page reload.
  var cartContentEl = document.getElementById('cart-content');
  if (cartContentEl) {
    var refreshTimer = null;
    new MutationObserver(function () {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(refreshTradeInCartState, 150);
    }).observe(cartContentEl, { childList: true, subtree: true });
  }
})();
